import { useState, useEffect, useCallback } from 'react';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import Layout from '../../components/Layout';
import useAuthStore from '../../store/authStore';
import { getPlanLimit } from '../../utils/planAccess';
import { confirm } from '../../components/ConfirmDialog';
import { printKitchen, isPrintingAvailable } from '../../utils/printService';
const emptyForm = { name: '', label: '', printer_type: 'thermal', is_receipt_printer: false, conn_type: 'usb', ip: '' };

export default function Printers() {
    const { user, waiterToken }     = useAuthStore();
    const [printers, setPrinters]   = useState([]);
    const [loading, setLoading]     = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm]           = useState(emptyForm);
    const [editId, setEditId]       = useState(null);
    const [saving, setSaving]       = useState(false);
    const [apiToken, setApiToken]   = useState(null); // /printers cavabından tenant token-i (fallback)
    const [testingId, setTestingId] = useState(null); // hazırda test çapı gedən printer

    // Mətbəx ekranı linki — token store-dan, yoxdursa /printers cavabından
    const kitchenTok = waiterToken || apiToken || '';
    const kitchenLink = (printerName) =>
        `${window.location.origin}/kitchen/?t=${kitchenTok}&printer=${encodeURIComponent(printerName)}`;
    const copyKitchen = (printerName) => {
        navigator.clipboard?.writeText(kitchenLink(printerName))
            .then(() => toast.success('Mətbəx linki kopyalandı.'))
            .catch(() => toast.error('Kopyalana bilmədi.'));
    };

    const maxPrinters   = getPlanLimit(user?.tenant_plan, 'maxPrinters'); // null = unlimited
    const printerLocked = !editId && maxPrinters !== null && printers.length >= maxPrinters;

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get('/printers');
            setPrinters(res.data.data || []);
            if (res.data.waiter_token) setApiToken(res.data.waiter_token);
        } catch {
            toast.error('Printerlər yüklənmədi.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openAdd = () => {
        if (printerLocked) {
            return toast.error(`Pulsuz planda maksimum ${maxPrinters} printer əlavə edilə bilər.`);
        }
        setEditId(null);
        setForm(emptyForm);
        setShowModal(true);
    };

    const openEdit = (p) => {
        setEditId(p.id);
        setForm({ name: p.name, label: p.label, printer_type: p.printer_type || 'thermal', is_receipt_printer: Boolean(Number(p.is_receipt_printer || 0)), conn_type: p.conn_type || 'usb', ip: p.ip || '' });
        setShowModal(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.label.trim()) {
            return toast.error('Ad və etiket lazımdır.');
        }
        if (form.conn_type === 'lan' && !String(form.ip || '').trim()) {
            return toast.error('LAN printer üçün IP ünvanı yazın.');
        }
        setSaving(true);
        try {
            if (editId) {
                await api.put(`/printers/${editId}`, { ...form, active: 1 });
                toast.success('Printer yeniləndi.');
            } else {
                await api.post('/printers', form);
                toast.success('Printer əlavə edildi.');
            }
            setShowModal(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!(await confirm('Bu printeri silmək istəyirsiniz?'))) return;
        try {
            await api.delete(`/printers/${id}`);
            toast.success('Printer silindi.');
            load();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    // Test çap — printeri yoxla. printKitchen eyni yolla gedir (USB→C# GDI, LAN→C# TCP)
    // və cari "mətbəx iri font" tənzimləməsini əks etdirir, ona görə iri fontu da yoxlayır.
    const handleTestPrint = async (p) => {
        if (!(await isPrintingAvailable())) {
            return toast.error('Çap yalnız Fiscal POS desktop proqramında işləyir.');
        }
        setTestingId(p.id);
        const now = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const stamp = `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
        const conn = p.conn_type === 'lan' ? `LAN ${p.ip}` : 'USB / Lokal';
        const text = [
            '===== TEST ÇAP =====',
            `${p.label}  (${p.name})`,
            conn,
            stamp,
            '--------------------',
            'Nümunə sətir 1',
            'Kabab            2',
            'Cola             1',
            '--------------------',
            'Bu bir test çapıdır.',
            'Fiscal POS',
            '',
        ].join('\n');
        try {
            await printKitchen(p.name, text, p.conn_type === 'lan' ? (p.ip || '') : '');
            toast.success(`Test çapı göndərildi → ${p.label}`);
        } catch (err) {
            toast.error(err?.message || 'Test çapı göndərilmədi.');
        } finally {
            setTestingId(null);
        }
    };

    const handleToggleActive = async (p) => {
        try {
            await api.put(`/printers/${p.id}`, {
                name: p.name, label: p.label, active: p.active ? 0 : 1, printer_type: p.printer_type || 'thermal',
                is_receipt_printer: Number(p.is_receipt_printer || 0), conn_type: p.conn_type || 'usb', ip: p.ip || ''
            });
            load();
        } catch {
            toast.error('Xəta baş verdi.');
        }
    };

    return (
        <Layout>
            <div className="max-w-2xl mx-auto">
                {/* Geri */}
                <a href="/admin/settings"
                   className="inline-flex items-center gap-1.5 mb-4 text-sm font-semibold text-slate-500 hover:text-slate-700">
                    ← Tənzimləmələrə qayıt
                </a>
                {/* Header */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                    <div>
                        <h1 className="text-2xl font-black text-gray-900">🖨️ Printerlər</h1>
                        <p className="text-sm text-gray-400 mt-0.5">Mətbəx və kassa printerlərini idarə edin</p>
                    </div>
                    <button
                        onClick={openAdd}
                        className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-all shadow-sm ${
                            printerLocked
                                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                    >
                        {printerLocked ? `🔒 Limit: ${maxPrinters} printer` : '+ Printer əlavə et'}
                    </button>
                </div>

                    {/* Çap məlumatı */}
                    <div className="mb-4 rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4">
                        <div className="flex items-center gap-2">
                            <span className="text-lg">🖨️</span>
                            <h3 className="font-bold text-gray-800 text-sm">Çap</h3>
                        </div>
                        <p className="text-[12px] mt-2 leading-relaxed text-emerald-700">
                            ✅ Çap birbaşa Fiscal POS proqramından edilir — bu PC-yə qoşulu printerlərə.
                        </p>
                        <p className="text-[10px] text-gray-500 mt-1">
                            Qeyd: çap yalnız Fiscal POS desktop proqramında işləyir (telefon/brauzerdə yox).
                        </p>
                    </div>

                    {/* Help box */}
                    <div className="mb-4 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-xs text-blue-700">
                        <strong>Ad (name)</strong> — texniki ad (məs: <code>xp-metbex</code>). Menyu məhsulları bu ada görə printerə bağlanır.<br />
                        <strong>Qoşulma</strong> — <strong>USB/Lokal</strong>: çap Windows-da bu adla quraşdırılmış printerə gedir. <strong>LAN (IP)</strong>: çap birbaşa printerin IP-sinə gedir — Windows sürücüsü/portu lazım deyil.
                    </div>

                    {/* List */}
                    {loading ? (
                        <div className="py-16 text-center text-gray-400">Yüklənir...</div>
                    ) : printers.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-gray-200 bg-white py-16 text-center">
                            <p className="text-4xl mb-3">🖨️</p>
                            <p className="text-gray-500 font-semibold">Hələ printer yoxdur</p>
                            <p className="text-sm text-gray-400 mt-1">Yeni printer əlavə etmək üçün yuxarıdakı düyməni basın</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {printers.map(p => (
                                <div
                                    key={p.id}
                                    className={`flex flex-wrap items-center justify-between gap-2 rounded-xl border px-4 py-3 bg-white transition-all ${p.active ? 'border-gray-200' : 'border-gray-100 opacity-50'}`}
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <span className="text-xl shrink-0">🖨️</span>
                                        <div className="min-w-0">
                                            <p className="font-bold text-gray-800 text-sm flex items-center gap-1.5">
                                                {p.label}
                                                <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 ${
                                                    p.printer_type === 'a4'
                                                        ? 'bg-indigo-100 text-indigo-700'
                                                        : 'bg-orange-100 text-orange-700'
                                                }`}>
                                                    {p.printer_type === 'a4' ? '📄 A4' : '🧾 Termal'}
                                                </span>
                                            </p>
                                            <p className="text-xs text-gray-400 font-mono mt-0.5">
                                                {p.name}
                                                {p.conn_type === 'lan'
                                                    ? <span className="ml-2 text-blue-600">🌐 {p.ip}</span>
                                                    : <span className="ml-2 text-gray-400">🔌 USB</span>}
                                            </p>
                                            {/* Köhnə mətbəx ekranı linki buradan çıxarıldı — indi superadmin → Linklər (şifrəsiz) */}
                                        </div>
                                        {!p.active && (
                                            <span className="text-[10px] font-semibold text-red-500 bg-red-50 rounded-full px-2 py-0.5 shrink-0">
                                                İşləmir — çek getmir
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <button
                                            onClick={() => handleTestPrint(p)}
                                            disabled={testingId === p.id}
                                            title="Test çapı göndər — printeri yoxla"
                                            className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 transition-all disabled:opacity-60"
                                        >
                                            {testingId === p.id ? '⏳ Gedir...' : '🖨️ Test çap'}
                                        </button>
                                        <button
                                            onClick={() => handleToggleActive(p)}
                                            title={p.active ? 'İşləmir et — bu printerə mətbəx çeki göndərilməyəcək' : 'İşlək et — mətbəx çeki yenidən göndəriləcək'}
                                            className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all border ${
                                                p.active
                                                    ? 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100'
                                                    : 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
                                            }`}
                                        >
                                            {p.active ? '⏸ İşləmir et' : '▶ İşlək et'}
                                        </button>
                                        <button
                                            onClick={() => openEdit(p)}
                                            className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 transition-all"
                                        >
                                            ✏️
                                        </button>
                                        <button
                                            onClick={() => handleDelete(p.id)}
                                            className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100 transition-all"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
            </div>

            {/* Modal */}
            {showModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                    onClick={e => e.target === e.currentTarget && setShowModal(false)}
                >
                    <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
                        <div className="border-b border-gray-100 px-5 py-4">
                            <h2 className="text-base font-bold text-gray-800">
                                {editId ? 'Printer redaktə et' : 'Yeni printer əlavə et'}
                            </h2>
                        </div>
                        <form onSubmit={handleSave} className="p-5 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                    Texniki ad (name) <span className="text-red-400">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={e => setForm(f => ({ ...f, name: e.target.value.trim() }))}
                                    placeholder="məs: xp-kassa, xp-metbex"
                                    className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-blue-400 transition-all"
                                    autoFocus
                                />
                                <p className="text-[11px] text-gray-400 mt-1">Boşluqsuz, kiçik hərf, defis istifadə edin (məs: xp-kassa)</p>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                    Etiket (label) <span className="text-red-400">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={form.label}
                                    onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                                    placeholder="məs: Kassa, Mətbəx, Bar"
                                    className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-blue-400 transition-all"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                    Printer növü <span className="text-red-400">*</span>
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    {[
                                        { key: 'thermal', label: '🧾 XPrinter (Termal)', desc: 'Çek, mətbəx' },
                                        { key: 'a4',      label: '📄 A4 Printer',        desc: 'Texkart, sayım' },
                                    ].map(opt => (
                                        <button
                                            key={opt.key}
                                            type="button"
                                            onClick={() => setForm(f => ({ ...f, printer_type: opt.key }))}
                                            className={`rounded-xl px-3 py-2 text-left border-2 transition-all ${
                                                form.printer_type === opt.key
                                                    ? 'border-blue-500 bg-blue-50'
                                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                            }`}>
                                            <div className="text-xs font-bold text-gray-800">{opt.label}</div>
                                            <div className="text-[10px] text-gray-500 mt-0.5">{opt.desc}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                    Qoşulma <span className="text-red-400">*</span>
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    {[
                                        { key: 'usb', label: '🔌 USB / Lokal', desc: 'Windows printer adı ilə' },
                                        { key: 'lan', label: '🌐 LAN (IP)',     desc: 'Birbaşa IP-yə, sürücüsüz' },
                                    ].map(opt => (
                                        <button
                                            key={opt.key}
                                            type="button"
                                            onClick={() => setForm(f => ({ ...f, conn_type: opt.key }))}
                                            className={`rounded-xl px-3 py-2 text-left border-2 transition-all ${
                                                form.conn_type === opt.key
                                                    ? 'border-blue-500 bg-blue-50'
                                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                            }`}>
                                            <div className="text-xs font-bold text-gray-800">{opt.label}</div>
                                            <div className="text-[10px] text-gray-500 mt-0.5">{opt.desc}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {form.conn_type === 'lan' ? (
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                        IP ünvanı <span className="text-red-400">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={form.ip}
                                        onChange={e => setForm(f => ({ ...f, ip: e.target.value.trim() }))}
                                        placeholder="məs: 192.168.100.15"
                                        className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-blue-400 transition-all"
                                    />
                                    <p className="text-[11px] text-gray-400 mt-1">Çap birbaşa bu IP-yə (port 9100) gedir — Windows sürücüsü/portu lazım deyil.</p>
                                </div>
                            ) : (
                                <p className="text-[11px] text-gray-400 -mt-1">USB/lokal: çap Windows-da bu <strong>texniki ad</strong> ilə quraşdırılmış printerə gedir.</p>
                            )}

                            <label className="flex items-start gap-2 rounded-xl border border-emerald-100 bg-emerald-50/40 p-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={Boolean(form.is_receipt_printer)}
                                    onChange={(e) => setForm(f => ({ ...f, is_receipt_printer: e.target.checked }))}
                                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600"
                                />
                                <div className="flex-1">
                                    <div className="text-sm font-bold text-emerald-800">🧾 Müştəri çek printeri</div>
                                    <div className="text-[11px] text-emerald-700/80 mt-0.5 leading-snug">
                                        Müştəri çek çapı bu printerə göndərilir. Eyni vaxtda yalnız BİR printer seçilə bilər. Bu printer eyni zamanda mətbəxə də xidmət edə bilər.
                                    </div>
                                </div>
                            </label>

                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-500 hover:bg-gray-50 transition-all"
                                >
                                    Ləğv et
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="flex-1 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white py-2.5 text-sm font-bold transition-all"
                                >
                                    {saving ? 'Saxlanır...' : (editId ? 'Yenilə' : 'Əlavə et')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Layout>
    );
}
